require('./fragment.sass');
const u = up.util;
const e = up.element;
up.fragment = (function () {
    function upTagName(element) {
        let tagName = e.tagName(element);
        if (tagName.startsWith('up-')) {
            return tagName;
        }
    }
    const STRONG_TARGET_DERIVERS = [
        '[up-id]',
        '[id]',
        'html',
        'head',
        'body',
    ];
    const config = new up.Config(() => ({
        badTargetClasses: [/^up-/],
        strongTargetDerivers: STRONG_TARGET_DERIVERS,
        targetDerivers: [
            ...STRONG_TARGET_DERIVERS,
            'main',
            '[up-main]',
            upTagName,
            'link[rel][type]',
            'link[rel=preload][href]',
            'link[rel=preconnect][href]',
            'link[rel=prefetch][href]',
            'link[rel]',
            'meta[property]',
            '*[name]',
            'form[action]',
            'a[href]',
            '[class]',
            '[up-flashes]',
            'form',
        ],
        verifyDerivedTarget: true,
        renderOptions: {
            hungry: true,
            keep: true,
            saveScroll: true,
            saveFocus: true,
            focusVisible: 'auto',
            abort: 'target',
            failOptions: true,
            feedback: true,
        },
        navigateOptions: {
            cache: 'auto',
            revalidate: 'auto',
            fallback: true,
            focus: 'auto',
            scroll: 'auto',
            history: 'auto',
            peel: 'dismiss',
        },
        reloadOptions: {
            target: ':main',
            scroll: 'keep',
            focus: 'keep',
        },
        match: 'region',
        autoHistoryTargets: [':main'],
        autoFocus: ['hash', 'autofocus', 'main-if-main', 'keep', 'target-if-lost'],
        autoScroll: ['hash', 'layer-if-main'],
        autoRevalidate: (response) => response.expired,
        skipResponse: defaultSkipResponse,
        renderableResponse: (response) => response.isHTML(),
        normalizeKeepHTML: defaultNormalizeKeepHTML
    }));
    u.delegate(config, ['mainTargets'], () => up.layer.config.any);
    function defaultSkipResponse({ response, expiredResponse }) {
        return !response.text || response.text === expiredResponse?.text;
    }
    function sourceOf(element, options = {}) {
        element = getSmart(element, options);
        return e.closestAttr(element, 'up-source');
    }
    function normalizeSource(source) {
        return u.normalizeURL(source, { hash: false });
    }
    function timeOf(element) {
        let value = e.closestAttr(element, 'up-time');
        if (value && value !== 'false') {
            if (/^\d+$/.test(value)) {
                value = Number(value) * 1000;
            }
            return new Date(value);
        }
    }
    function etagOf(element) {
        let value = e.closestAttr(element, 'up-etag');
        if (value && value !== 'false') {
            return value;
        }
    }
    const render = up.mockable((...args) => {
        let options = parseTargetAndOptions(args);
        return new up.RenderJob(options).execute();
    });
    const navigate = up.mockable((...args) => {
        const options = parseTargetAndOptions(args);
        return render({ ...options, navigate: true });
    });
    function emitFragmentKeep({ oldElement, newElement: newFragment, newData, renderOptions }) {
        const log = ['Keeping fragment %o', oldElement];
        const callback = up.script.callbackAttr(oldElement, 'up-on-keep', { expandObject: ['newFragment', 'newData'] });
        return up.emit(oldElement, 'up:fragment:keep', { log, callback, newFragment, newData, renderOptions });
    }
    function emitFragmentKept({ oldElement, newElement: newFragment, newData }) {
        return up.emit(oldElement, 'up:fragment:kept', { log: false, newFragment, newData });
    }
    function findKeepPlan(options) {
        if (options.keep === false)
            return;
        const { oldElement, newElement } = options;
        let oldElementMode = keepMode(oldElement);
        if (!oldElementMode) {
            return;
        }
        let partner;
        let partnerSelector = up.fragment.tryToTarget(oldElement);
        if (!partnerSelector) {
            up.warn('[up-keep]', 'Cannot keep untargetable %o', oldElement);
        }
        const lookupOpts = { layer: options.layer };
        if (options.descendantsOnly) {
            partner = up.fragment.get(newElement, partnerSelector, lookupOpts);
        }
        else {
            partner = e.subtreeFirst(newElement, partnerSelector, lookupOpts);
        }
        if (!partner)
            return;
        let partnerMode = keepMode(partner);
        if (partnerMode === false)
            return;
        let oldIdentity = keepIdentity(oldElement, oldElementMode);
        let partnerIdentity = keepIdentity(partner, oldElementMode);
        if (!u.isEqual(oldIdentity, partnerIdentity))
            return;
        const plan = {
            oldElement,
            newElement: partner,
            newData: up.script.data(partner),
            renderOptions: options,
        };
        if (emitFragmentKeep(plan).defaultPrevented)
            return;
        return plan;
    }
    function keepIdentity(element, mode = keepMode(element)) {
        return element.upKeepIdentity ??= u.copy(buildKeepIdentity(element, mode));
    }
    function defaultNormalizeKeepHTML(html) {
        html = e.removeAttrsFromHTML(html, ['up-etag', 'up-source', 'up-time', 'nonce', ...up.script.config.nonceableAttributes]);
        html = html.replace(/^[ \t]+/mg, '');
        return html;
    }
    function buildKeepIdentity(element, mode) {
        if (mode === 'same-html') {
            return config.normalizeKeepHTML(element.outerHTML);
        }
        else if (mode === 'same-data') {
            return up.data(element);
        }
        else {
            return true;
        }
    }
    up.macro('[up-keep]', (element) => keepIdentity(element));
    function keepMode(element) {
        return e.booleanOrStringAttr(element, 'up-keep');
    }
    function emitFragmentInserted(element, meta) {
        if (element.upInserted)
            return;
        element.upInserted = true;
        const log = ['Inserted fragment %o', element];
        return mutate(() => up.emit(element, 'up:fragment:inserted', { ...meta, log }));
    }
    function emitFragmentDestroyed(fragment, options) {
        const log = options.log ?? ['Destroyed fragment %o', fragment];
        const parent = options.parent || document;
        return mutate(() => up.emit(parent, 'up:fragment:destroyed', { fragment, parent, log }));
    }
    function isNotDestroying(element) {
        return !element.closest('.up-destroying');
    }
    function isAlive(fragment) {
        return fragment.isConnected && isNotDestroying(fragment);
    }
    function getSmart(...args) {
        let [root, selector, options] = parseGetArgs(args);
        if (u.isElementLike(selector)) {
            return e.get(selector);
        }
        if (root) {
            return getFirstDescendant(root, selector, options);
        }
        return new up.FragmentFinder({
            selector,
            ...u.pick(options, ['layer', 'match', 'origin', 'destroying']),
        }).find();
    }
    function getFirstDescendant(...args) {
        let [root, selectorString, options] = parseGetArgs(args);
        let selector = new up.Selector(selectorString, root, options);
        return selector.firstDescendant(root);
    }
    function parseGetArgs(args) {
        return u.args(args, 'val', 'val', 'options');
    }
    function getAll(...args) {
        let [root, selectorString, options] = parseGetArgs(args);
        if (u.isElement(selectorString)) {
            return [selectorString];
        }
        if (u.isList(selectorString)) {
            return selectorString;
        }
        let selector = new up.Selector(selectorString, root, options);
        return selector.descendants(root);
    }
    function getSubtree(element, selector, options = {}) {
        return new up.Selector(selector, element, options).subtree(element);
    }
    function contains(root, selectorOrElement) {
        if (u.isElement(selectorOrElement)) {
            return e.contains(root, selectorOrElement) && up.layer.get(root).contains(selectorOrElement);
        }
        else {
            return getSubtree(root, selectorOrElement).length > 0;
        }
    }
    function closest(element, selector, options) {
        return new up.Selector(selector, element, options).closest(element);
    }
    function destroy(...args) {
        const options = parseTargetAndOptions(args);
        const element = getSmart(options.target, options);
        if (element) {
            new up.Change.DestroyFragment({ ...options, element }).execute();
        }
        return up.migrate.formerlyAsync?.('up.destroy()');
    }
    function parseTargetAndOptions(args) {
        const options = u.parseArgIntoOptions(args, 'target');
        if (u.isElement(options.target)) {
            options.origin ||= options.target;
        }
        return options;
    }
    function markFragmentAsDestroying(element) {
        element.classList.add('up-destroying');
        element.setAttribute('inert', '');
    }
    function reload(...args) {
        const options = { ...config.reloadOptions, ...parseTargetAndOptions(args) };
        options.target ||= ':main';
        const element = getSmart(options.target, options);
        options.url ||= sourceOf(element);
        options.headers = u.merge(options.headers, conditionalHeaders(element));
        if (options.keepData || e.booleanAttr(element, 'up-keep-data')) {
            options.data = up.data(element);
        }
        up.migrate.postprocessReloadOptions?.(options);
        return render(options);
    }
    function conditionalHeaders(element) {
        let headers = {};
        let time = timeOf(element);
        if (time) {
            headers['If-Modified-Since'] = time.toUTCString();
        }
        let etag = etagOf(element);
        if (etag) {
            headers['If-None-Match'] = etag;
        }
        return headers;
    }
    function visit(url, options) {
        return navigate({ ...options, url });
    }
    const KEY_PATTERN = /^(onFail|on|fail)?(.+)$/;
    function successKey(key) {
        let match = KEY_PATTERN.exec(key);
        if (match) {
            let [_, prefix, suffix] = match;
            switch (prefix) {
                case 'onFail':
                    return 'on' + u.upperCaseFirst(suffix);
                case 'fail':
                    return u.lowerCaseFirst(suffix);
            }
        }
    }
    function failKey(key) {
        let match = KEY_PATTERN.exec(key);
        if (match) {
            let [_, prefix, suffix] = match;
            switch (prefix) {
                case 'on':
                    return 'onFail' + u.upperCaseFirst(suffix);
                case undefined:
                    return 'fail' + u.upperCaseFirst(suffix);
            }
        }
    }
    function toTarget(element, options) {
        return u.presence(element, u.isString) || tryToTarget(element, options) || cannotTarget(element);
    }
    function isTargetable(element, options) {
        return !!tryToTarget(element, options);
    }
    function untargetableMessage(element) {
        return `Cannot derive good target selector from a <${e.tagName(element)}> element without identifying attributes. Try setting an [id] or configure up.fragment.config.targetDerivers.`;
    }
    function cannotTarget(element) {
        throw new up.CannotTarget(untargetableMessage(element));
    }
    function tryToTarget(element, options = {}) {
        let derivers = options.strong ? config.strongTargetDerivers : config.targetDerivers;
        return u.findResult(derivers, function (deriver) {
            let target = deriveTarget(element, deriver);
            if (target && isGoodTarget(target, element, options)) {
                return target;
            }
        });
    }
    function deriveTarget(element, deriver) {
        if (u.isFunction(deriver)) {
            return deriver(element);
        }
        else if (element.matches(deriver)) {
            try {
                return deriveTargetFromPattern(element, deriver);
            }
            catch (e) {
                if (e instanceof up.CannotParse) {
                    return deriver;
                }
                else {
                    throw e;
                }
            }
        }
    }
    function deriveTargetFromPattern(element, deriver) {
        let { includePath, excludeRaw } = e.parseSelector(deriver);
        if (includePath.length !== 1) {
            throw new up.CannotParse(deriver);
        }
        let { tagName, id, classNames, attributes } = includePath[0];
        let result = '';
        if (tagName === '*') {
            result += e.tagName(element);
        }
        else if (tagName) {
            result += tagName;
        }
        for (let className of classNames) {
            result += e.classSelector(className);
        }
        if (id) {
            result += e.idSelector(id);
        }
        for (let attributeName in attributes) {
            let attributeValue = attributes[attributeName] || element.getAttribute(attributeName);
            if (attributeName === 'id') {
                result += e.idSelector(attributeValue);
            }
            else if (attributeName === 'class') {
                for (let goodClass of goodClassesForTarget(element)) {
                    result += e.classSelector(goodClass);
                }
            }
            else {
                result += e.attrSelector(attributeName, attributeValue);
            }
        }
        if (excludeRaw) {
            result += excludeRaw;
        }
        return result;
    }
    function isGoodTarget(target, element, options = {}) {
        let verify = options.verify ?? config.verifyDerivedTarget;
        return !isAlive(element) || !verify || up.fragment.get(target, { layer: element, ...options }) === element;
    }
    function matchesPattern(pattern, str) {
        if (u.isRegExp(pattern)) {
            return pattern.test(str);
        }
        else {
            return pattern === str;
        }
    }
    function goodClassesForTarget(element) {
        let isGood = (klass) => !u.some(config.badTargetClasses, (badTargetClass) => matchesPattern(badTargetClass, klass));
        return u.filter(element.classList, isGood);
    }
    const MAIN_PSEUDO = /:main\b/;
    const LAYER_PSEUDO = /:layer\b/;
    const ORIGIN_PSEUDO = /:origin\b/;
    function containsMainPseudo(target) {
        return MAIN_PSEUDO.test(target);
    }
    function expandTargets(targets, options = {}) {
        const { layer } = options;
        if (layer !== 'new' && !(layer instanceof up.Layer)) {
            up.fail('Must pass an up.Layer as { layer } option, but got %o', layer);
        }
        targets = u.copy(u.wrapList(targets));
        const expanded = [];
        while (targets.length) {
            let target = targets.shift();
            if (target === true)
                target = ':main';
            if (containsMainPseudo(target)) {
                let mode = resolveMode(options);
                let replaced = up.layer.mainTargets(mode).map((mainTarget) => target.replace(MAIN_PSEUDO, mainTarget));
                targets.unshift(...replaced);
            }
            else if (LAYER_PSEUDO.test(target)) {
                if (layer === 'new' || layer.state === 'opening')
                    continue;
                let firstSwappableTarget = toTarget(layer.getFirstSwappableElement(), options);
                targets.unshift(target.replace(LAYER_PSEUDO, firstSwappableTarget));
            }
            else if (u.isElementLike(target)) {
                expanded.push(toTarget(target, options));
            }
            else if (u.isString(target)) {
                expanded.push(resolveOrigin(target, options));
            }
        }
        return u.uniq(expanded);
    }
    function resolveMode({ layer, mode }) {
        if (layer === 'new') {
            return mode || up.fail('Must pass a { mode } option together with { layer: "new" }');
        }
        else {
            return layer.mode;
        }
    }
    function modernResolveOrigin(target, { origin } = {}) {
        return target.replace(ORIGIN_PSEUDO, function (match) {
            if (origin) {
                return toTarget(origin);
            }
            else {
                up.fail('Missing { origin } element to resolve "%s" reference (found in %s)', match, target);
            }
        });
    }
    function resolveOrigin(target, options) {
        if (!u.isString(target))
            return target;
        return (up.migrate.resolveOrigin || modernResolveOrigin)(target, options);
    }
    function splitTarget(target) {
        return u.getComplexTokens(target);
    }
    function parseTargetSteps(target, options = {}) {
        let defaultPlacement = options.defaultPlacement || 'swap';
        let defaultMaybe = options.defaultMaybe ?? false;
        let steps = [];
        let simpleSelectors = splitTarget(target);
        for (let selector of simpleSelectors) {
            if (selector === ':none')
                continue;
            let placement = defaultPlacement;
            let maybe = defaultMaybe;
            selector = selector.replace(/\b::?(before|after|content)\b/, (_match, customPlacement) => {
                placement = customPlacement;
                return '';
            });
            selector = selector.replace(/\b:maybe\b/, () => {
                maybe = true;
                return '';
            });
            const step = {
                ...options,
                selector,
                placement,
                maybe,
                originalRenderOptions: options,
            };
            steps.push(step);
        }
        return steps;
    }
    function hasAutoHistory(newFragments, layer) {
        let vanillaSelector = expandTargets(config.autoHistoryTargets, { layer }).join();
        for (let newFragment of newFragments) {
            if (e.subtree(newFragment, vanillaSelector).length) {
                return true;
            }
        }
        up.puts('up.render()', "Will not auto-update history because fragment doesn't contain a selector from up.fragment.config.autoHistoryTargets");
        return false;
    }
    function matches(element, selector, options = {}) {
        element = e.get(element);
        if (u.isElement(selector)) {
            let target = tryToTarget(selector);
            return target && element.matches(target);
        }
        else {
            return new up.Selector(selector, element, options).matches(element);
        }
    }
    function matchSelectorMap(element, selectorMap, { layer = element } = {}) {
        let hits = [];
        if (selectorMap && element) {
            for (let [selector, value] of Object.entries(selectorMap)) {
                if (u.isDefined(value) && matches(element, selector, { layer })) {
                    hits.push(value);
                }
            }
        }
        return hits;
    }
    function shouldRevalidate(request, response, options = {}) {
        return request.fromCache && u.evalAutoOption(options.revalidate, config.autoRevalidate, response);
    }
    function isContainedByRivalStep(steps, candidateStep) {
        return u.some(steps, function (rivalStep) {
            return (rivalStep !== candidateStep) &&
                ((rivalStep.placement === 'swap') || (rivalStep.placement === 'content')) &&
                rivalStep.oldElement.contains(candidateStep.oldElement);
        });
    }
    function compressNestedSteps(steps) {
        if (steps.length < 2)
            return steps;
        let compressed = u.uniqBy(steps, 'oldElement');
        compressed = u.reject(compressed, (step) => isContainedByRivalStep(compressed, step));
        return compressed;
    }
    function abort(...args) {
        let options = parseTargetAndOptions(args);
        let testFn;
        let { reason, newLayer, jid } = options;
        let elements;
        if (options.target) {
            elements = getAll(options.target, options);
            testFn = (request) => request.isBoundToSubtrees(elements);
            reason ||= 'Aborting requests within fragment';
        }
        else {
            let layers = up.layer.getAll(options);
            elements = u.map(layers, 'element');
            testFn = (request) => request.isBoundToLayers(layers);
            reason ||= 'Aborting requests within ' + layers.join(', ');
        }
        let testFnWithAbortable = (request) => request.abortable && testFn(request);
        up.network.abort(testFnWithAbortable, { ...options, reason });
        for (let element of elements) {
            up.emit(element, 'up:fragment:aborted', { reason, newLayer, jid, log: reason });
        }
    }
    function onAborted(fragment, callback) {
        return up.event.onClosest(fragment, 'up:fragment:aborted', callback);
    }
    function onKept(fragment, callback) {
        return up.event.onClosest(fragment, 'up:fragment:kept', callback);
    }
    function onFirstIntersect(element, callback, { margin = 0 } = {}) {
        if (e.isIntersectingWindow(element, { margin })) {
            callback();
            return;
        }
        function processIntersectEntries(entries) {
            for (let entry of entries) {
                if (entry.isIntersecting) {
                    disconnect();
                    callback();
                    return;
                }
            }
        }
        let observer = new IntersectionObserver(processIntersectEntries, { rootMargin: `${margin}px` });
        let disconnect = () => observer.disconnect();
        observer.observe(element);
        up.destructor(element, disconnect);
    }
    const STARTS_WITH_SELECTOR = /^([\w-]+|\*)?(#|\.|[:[][a-z-]{3,})/;
    function provideNodes(value, { origin, originLayer, data, htmlParser = e.createNodesFromHTML } = {}) {
        if (u.isString(value) && STARTS_WITH_SELECTOR.test(value)) {
            let [parsedValue, parsedData] = u.parseScalarJSONPairs(value)[0];
            data = { ...data, ...parsedData };
            value = up.fragment.get(parsedValue, { layer: 'closest', origin, originLayer }) || up.fail(`Cannot find template "%s"`, value);
        }
        if (u.isString(value)) {
            value = htmlParser(value);
        }
        if (isTemplate(value)) {
            value = cloneTemplate(value, data, { htmlParser });
        }
        return u.wrapList(value);
    }
    function isTemplate(value) {
        return u.isElement(value) && value.matches('template, script[type]') && !up.script.isScript(value);
    }
    function cloneTemplate(templateOrSelector, data = {}, { origin, htmlParser } = {}) {
        let template = getSmart(templateOrSelector, { origin }) || up.fail('Template not found: %o', templateOrSelector);
        let event = up.emit(template, 'up:template:clone', { data, nodes: null, log: ["Using template %o", templateOrSelector] });
        let nodes = event.nodes ?? defaultTemplateNodes(template, htmlParser);
        for (let node of nodes) {
            node.upTemplateData = data;
        }
        return nodes;
    }
    function defaultTemplateNodes(template, htmlParser = e.createNodesFromHTML) {
        let templateText = template.innerHTML;
        return htmlParser(templateText);
    }
    function insertTemp(...args) {
        let [reference, position = 'beforeend', tempValue] = u.args(args, 'val', u.isAdjacentPosition, 'val');
        let tempNodes = provideNodes(tempValue, { origin: reference });
        let tempElement = e.wrapIfRequired(tempNodes);
        let oldPosition = document.contains(tempElement) && e.documentPosition(tempElement);
        reference.insertAdjacentElement(position, tempElement);
        if (oldPosition) {
            return () => {
                oldPosition[0].insertAdjacentElement(oldPosition[1], tempElement);
            };
        }
        else {
            up.hello(tempElement);
            return () => up.destroy(tempElement);
        }
    }
    function trackSelector(...args) {
        let parsedArgs = u.args(args, 'val', 'options', 'callback');
        let tracker = new up.SelectorTracker(...parsedArgs);
        return tracker.start();
    }
    let mutateLevel = 0;
    let afterMutateCleaner = u.cleaner('fifo');
    function mutate(callback) {
        try {
            mutateLevel++;
            return callback();
        }
        finally {
            if (--mutateLevel === 0)
                afterMutateCleaner.clean();
        }
    }
    function afterMutate(callback) {
        if (mutateLevel === 0) {
            callback();
        }
        else {
            afterMutateCleaner(callback);
        }
    }
    up.on('up:framework:boot', function () {
        const { documentElement } = document;
        up.hello(documentElement);
        documentElement.setAttribute('up-source', normalizeSource(location.href));
        if (!up.browser.canPushState()) {
            return up.warn('Cannot push history changes. Next render pass with history will load a full page.');
        }
    });
    return {
        config,
        reload,
        destroy,
        render,
        navigate,
        get: getSmart,
        getFirstDescendant,
        all: getAll,
        subtree: getSubtree,
        contains,
        closest,
        source: sourceOf,
        normalizeSource,
        visit,
        markAsDestroying: markFragmentAsDestroying,
        emitInserted: emitFragmentInserted,
        emitDestroyed: emitFragmentDestroyed,
        emitKeep: emitFragmentKeep,
        emitKept: emitFragmentKept,
        keepPlan: findKeepPlan,
        defaultNormalizeKeepHTML,
        successKey,
        failKey,
        expandTargets,
        resolveOrigin,
        toTarget,
        tryToTarget,
        isTargetable,
        matches,
        matchSelectorMap,
        hasAutoHistory,
        time: timeOf,
        etag: etagOf,
        shouldRevalidate,
        abort,
        onAborted,
        onKept,
        onFirstIntersect,
        splitTarget,
        parseTargetSteps,
        isAlive,
        isNotDestroying,
        compressNestedSteps,
        containsMainPseudo,
        insertTemp,
        provideNodes,
        cloneTemplate,
        trackSelector,
        mutate,
        afterMutate,
    };
})();
up.reload = up.fragment.reload;
up.destroy = up.fragment.destroy;
up.render = up.fragment.render;
up.navigate = up.fragment.navigate;
up.visit = up.fragment.visit;
up.template = { clone: up.fragment.cloneTemplate };
u.delegate(up, ['context'], () => up.layer.current);
